/**
 * Course Materials Page Controller
 * Main coordinator for the course materials page functionality
 */
function cmrpStudentIdLabel() {
  if (typeof window !== 'undefined' && typeof window.getCmrpStudentIdLabel === 'function') {
    return window.getCmrpStudentIdLabel();
  }
  const s = typeof window !== 'undefined' ? window.shopifySettings?.cmrpFlowSwitcherStudentPlaceholder : undefined;
  const t = s != null ? String(s).trim() : '';
  return t || 'Student ID';
}

class CourseMaterialsPage {
  constructor(courseData) {
    this.courseData = courseData;
    this.selectedFilters = [];
    this.components = {};
    this.previousCampusValue = '';
    this.warningId =
      typeof window.cmrpId === 'function'
        ? window.cmrpId('student-id-error-warning', 'sid')
        : 'student-id-error-warning';
    this.mobileWarningId = 'mobile-student-id-error-warning';

    this.URL_PARAMS = {
      SHOP_BY: 'shopBy',
      TERM_ID: 'termId',
      DIVISION_DISPLAY_NAME: 'divisionDisplayName',
      DEPARTMENT_DISPLAY_NAME: 'departmentDisplayName',
      COURSE_DISPLAY_NAME: 'courseDisplayName',
      SECTION_DISPLAY_NAME: 'sectionDisplayName',
    };

    this.init();
  }

  /**
   * Initialize the page and all its components
   */
  init() {
    // Add page class for styling
    document.body.classList.add('course-materials-page');

    // Initialize components
    this.initializeComponents();

    // Setup event listeners
    this.setupEventListeners();

    // Initialize UI state
    this.initializeUI();

    // Auto-open sidebar on desktop
    this.handleDesktopSidebar();
  }

  /**
   * Initialize all major components
   */
  initializeComponents() {
    // Initialize sidebar
    const SidebarClass = window.CourseMaterialsSidebar;
    if (typeof SidebarClass === 'undefined') {
      console.error('CourseMaterialsSidebar is not defined. Make sure course-materials-sidebar.js is loaded first.');
      return;
    }
    this.components.sidebar = new SidebarClass({
      ddcs_campus_entity: this.courseData.ddcs_campus_entity,
      ddcs_campus_division: this.courseData.ddcs_campus_division,
      ddcs_term: this.courseData.ddcs_term,
      ddcs_campus_department: this.courseData.ddcs_campus_department,
      ddcs_course: this.courseData.ddcs_course,
      ddcs_section: this.courseData.ddcs_section,
      selectedFilters: this.selectedFilters,
    });

    // Wait for dependencies then initialize SOLID system
    this.initializeSOLIDSystem();
  }

  /**
   * Initialize the SOLID system components
   */
  initializeSOLIDSystem() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo

    const checkDependencies = () => {
      attempts++;

      // Check missing dependencies
      const missing = [];
      if (!window.ProductSystem?.SOLIDProductHandler)
        missing.push('ProductSystem.SOLIDProductHandler');
      if (!window.ProductDisplayManager) missing.push('ProductDisplayManager');
      if (!window.SelectionManager) missing.push('SelectionManager');
      if (!window.NavigationUIManager) missing.push('NavigationUIManager');
      if (!window.URLStateManager) missing.push('URLStateManager');
      if (!window.MaterialInteractionManager)
        missing.push('MaterialInteractionManager');
      if (!window.UIStateManager) missing.push('UIStateManager');
      if (missing.length > 0) {
        if (attempts >= maxAttempts) {
          alert(
            'Error: Failed to load course materials system. Please refresh the page.'
          );
          return;
        }

        setTimeout(checkDependencies, 100);
        return;
      }

      try {
        this.setupSOLIDComponents();
        this.setupTemplateFunctions();
        this.components.urlManager.initialize();
        this.components.uiStateManager.initialize();
      } catch (error) {
        alert('Error during system initialization. Please refresh the page.');
      }
    };

    checkDependencies();
  }
  updateSOLIDComponents() {
    // Product Handler
    this.components.productHandler =
      window.ProductSystem.SOLIDProductHandler.createForShopify(
        this.courseData.products_Materials,
        this.courseData.ddcs_section
      );

    // Display Manager
    this.components.displayManager = new window.ProductDisplayManager({
      products: this.courseData.products_Materials,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      productHandler: this.components.productHandler,
    });
    //console.log("displayManager ==> ", this.components.displayManager)

    // Selection Manager
    this.components.selectionManager = new window.SelectionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
    });

    // Navigation Manager
    this.components.navigationManager = new window.NavigationUIManager({
      selectedFilters: this.selectedFilters,
      products: this.courseData.products_Materials,
    });

    // URL Manager
    this.components.urlManager = new window.URLStateManager({
      selectedFilters: this.selectedFilters,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      ddcsCampusDepartment: this.courseData.ddcs_campus_department,
      ddcsCourse: this.courseData.ddcs_course,
      addLabelCallback: objCourse => this.addLabel(objCourse),
      refreshProductsCallback: () => window.refreshProductsDisplay(),
    });

    // Interaction Manager
    this.components.interactionManager = new window.MaterialInteractionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
      updateSelectedProductsCallback: () =>
        this.components.selectionManager.updateSelectedProducts(),
      updateSelectionBarSummaryCallback: () =>
        this.components.selectionManager.updateSelectionBarSummary(),
    });

    // UI State Manager
    this.components.uiStateManager = new window.UIStateManager();

    // Expose global functions for backward compatibility
    this.exposeGlobalFunctions();
  }
  /**
   * Setup SOLID architecture components
   */
  setupSOLIDComponents() {
    // Product Handler
    this.components.productHandler =
      window.ProductSystem.SOLIDProductHandler.createForShopify(
        this.courseData.products_Materials,
        this.courseData.ddcs_section
      );

    // Display Manager
    this.components.displayManager = new window.ProductDisplayManager({
      products: this.courseData.products_Materials,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      productHandler: this.components.productHandler,
    });
    //console.log("displayManager ==> ", this.components.displayManager)

    // Selection Manager
    this.components.selectionManager = new window.SelectionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
    });

    // Navigation Manager
    this.components.navigationManager = new window.NavigationUIManager({
      selectedFilters: this.selectedFilters,
      products: this.courseData.products_Materials,
    });

    // URL Manager
    this.components.urlManager = new window.URLStateManager({
      selectedFilters: this.selectedFilters,
      ddcsSection: this.courseData.ddcs_section,
      ddcsCampusEntity: this.courseData.ddcs_campus_entity,
      ddcsTerm: this.courseData.ddcs_term,
      ddcsCampusDepartment: this.courseData.ddcs_campus_department,
      ddcsCourse: this.courseData.ddcs_course,
      addLabelCallback: objCourse => this.addLabel(objCourse),
      refreshProductsCallback: () => window.refreshProductsDisplay(),
    });

    // Interaction Manager
    this.components.interactionManager = new window.MaterialInteractionManager({
      products: this.courseData.products_Materials,
      productHandler: this.components.productHandler,
      updateSelectedProductsCallback: () =>
        this.components.selectionManager.updateSelectedProducts(),
      updateSelectionBarSummaryCallback: () =>
        this.components.selectionManager.updateSelectionBarSummary(),
    });

    // UI State Manager
    this.components.uiStateManager = new window.UIStateManager();

    // Expose global functions for backward compatibility
    this.exposeGlobalFunctions();
  }

  /**
   * Setup template functions for backward compatibility
   */
  setupTemplateFunctions() {
    const handler = this.components.productHandler;

    // Product handler functions
    window.generateFormatButtons = handler.generateFormatButtons.bind(handler);
    window.generateInitialPurchaseOptions =
      handler.generateInitialPurchaseOptions.bind(handler);
    window.extractAuthorName = handler.extractAuthorName.bind(handler);
    window.extractEditionFromTitle =
      handler.extractEditionFromTitle.bind(handler);
    window.extractISBNFromProduct =
      handler.extractISBNFromProduct.bind(handler);
    window.extractPublisherName = handler.extractPublisherName.bind(handler);
    window.extractPublishedDate = handler.extractPublishedDate.bind(handler);

    window.extractPublisher = handler.extractPublisher.bind(handler);
    window.extractFormat = handler.extractFormat.bind(handler);
    window.extractCopyRight = handler.extractCopyRight.bind(handler);
    window.extractPublication = handler.extractPublication.bind(handler);

    window.getLowestPriceForFormat =
      handler.getLowestPriceForFormat.bind(handler);
    window.getSelectedFormat = handler.getSelectedFormat.bind(handler);
    window.findVariant = handler.findVariant.bind(handler);

    // Utility functions
    window.extractPublisherCode = product =>
      handler.extractPublisherName(product);
    window.extractMTCTitleId = product =>
      product.id?.toString() || 'Not available';
    window.formatDate = dateString => {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch (e) {
        return dateString;
      }
    };
    window.getVariantByOptions = (product, format, type, condition = 'New') => {
      return handler.priceCalculator.getVariantByOptions(product, format, type);
    };
    window.getCheckboxData = checkbox => {
      const materialItem = checkbox.closest('.material-item');
      const selectedFormat = handler.getSelectedFormat(materialItem);

      // Check if this is a simple product (determined by html-renderer based on variant data)
      const formatBtns = materialItem?.querySelectorAll('.format-btn') || [];
      const isSimple = formatBtns.length === 0;

      return {
        productId: checkbox.dataset.productId,
        selectedFormat: selectedFormat || (isSimple ? 'simple' : null),
        purchaseType: checkbox.dataset.purchaseType,
        productTitle: checkbox.dataset.productTitle,
        isSimpleProduct: isSimple,
      };
    };

    // Interaction manager functions
    window.handleFormatSelection =
      this.components.interactionManager.handleFormatSelection;
    window.updatePricingForFormat =
      this.components.interactionManager.updatePricingForFormat;
    window.togglePurchaseOption = (element, event) => {
      try {
        return this.components.interactionManager.togglePurchaseOption(
          element,
          event
        );
      } catch (error) {
        // Silent error handling
      }
    };
    window.handlePurchaseSelection = element => {
      try {
        return this.components.interactionManager.handlePurchaseSelection(
          element
        );
      } catch (error) {
        // Silent error handling
      }
    };
    window.updateSelectedProducts =
      this.components.selectionManager.updateSelectedProducts;
    window.updateSelectionBarSummary =
      this.components.selectionManager.updateSelectionBarSummary;
    window.addMaterialEventListeners =
      this.components.interactionManager.addMaterialEventListeners;
    window.toggleViewMore = this.components.interactionManager.toggleViewMore;
    window.populateExpandedDetails =
      this.components.interactionManager.populateExpandedDetails;
    window.getSelectedFormat =
      this.components.interactionManager.getSelectedFormat;
    window.getVariantByOptions =
      this.components.interactionManager.getVariantByOptions;

    // Navigation manager functions
    window.generateDepartmentChips =
      this.components.navigationManager.generateDepartmentChips;
    window.filterProductsByDepartment =
      this.components.navigationManager.filterProductsByDepartment;
    window.updateMaterialsCount =
      this.components.navigationManager.updateMaterialsCount;
    window.updateCampusTermDisplay =
      this.components.navigationManager.updateCampusTermDisplay;
    window.initializeDepartmentFilters =
      this.components.navigationManager.initializeDepartmentFilters;

    // URL manager functions
    window.setURLSearchParams = this.components.urlManager.setURLSearchParams;
    window.restoreFormStateFromURL =
      this.components.urlManager.restoreFormStateFromURL;
    window.buildFiltersFromURLParams =
      this.components.urlManager.buildFiltersFromURLParams;
    window.findMatchingEntities =
      this.components.urlManager.findMatchingEntities;
    window.createFilterObject = this.components.urlManager.createFilterObject;
    window.restoreDropdownsAndUI =
      this.components.urlManager.restoreDropdownsAndUI;
    window.setCampusDropdown = this.components.urlManager.setCampusDropdown;
    window.setTermDropdown = this.components.urlManager.setTermDropdown;
    window.populateTermsForCampus =
      this.components.urlManager.populateTermsForCampus;
    window.restoreAllFormFieldsFromURL =
      this.components.urlManager.restoreAllFormFieldsFromURL;
    window.restoreFieldsCascade =
      this.components.urlManager.restoreFieldsCascade;
    window.triggerDepartmentCascadeForRestore =
      this.components.urlManager.triggerDepartmentCascadeForRestore;
    window.attemptURLRestoration =
      this.components.urlManager.attemptURLRestoration;

    // UI state manager functions
    window.showLoadingState = this.components.uiStateManager.showLoadingState;
    window.hideLoadingState = this.components.uiStateManager.hideLoadingState;
    window.showEmptyState = this.components.uiStateManager.showEmptyState;
    window.showEmptyStateWithAdoption =
      this.components.uiStateManager.showEmptyStateWithAdoption;
    window.hideEmptyState = this.components.uiStateManager.hideEmptyState;
    window.createInlineLoader =
      this.components.uiStateManager.createInlineLoader;
    window.removeInlineLoader =
      this.components.uiStateManager.removeInlineLoader;
  }

  /**
   * Expose global functions for the page
   */
  exposeGlobalFunctions() {
    window.addSelectedProductsToCart =
      this.components.selectionManager.addSelectedProductsToCart;
    window.handleNotifyMe = this.components.productHandler.handleNotifyMe;

    window.refreshProductsDisplay = async () => {
      this.components.uiStateManager.showLoadingState();
      try {
        // Nav must see updated filters before generateDepartmentChips runs inside the display pass
        this.components.navigationManager.updateSelectedFilters(
          this.selectedFilters
        );
        await this.components.displayManager.refreshProductsDisplay(
          this.selectedFilters
        );
        this.components.navigationManager.updateSelectedFilters(
          this.selectedFilters
        );
        this.components.navigationManager.refreshNavigation();
        this.components.selectionManager.updateSelectionBarSummary();
      } catch (error) {
        this.components.uiStateManager.hideLoadingState();
      }
    };

    // Store reference to this instance
    window.courseMaterialsPage = this;
    window.courseMaterialsSidebar = this.components.sidebar;

    // Expose utility functions
    window.removeCourse = element => {
      try {
        return this.removeCourse(element);
      } catch (error) {
        console.error('removeCourse failed:', error);
      }
    };

    window.removeCourseSelection = (department, course, section, division) => {
      try {
        return this.removeCourseSelection(
          department,
          course,
          section,
          division,
        );
      } catch (error) {
        console.error('removeCourseSelection failed:', error);
      }
    };
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Sidebar events
    document.addEventListener('sidebar:courseAdded', event => {
      const detail = event.detail || {};
      const { course, suppressRefresh } = detail;

      if (course == null) {
        return;
      }

      // Use the new method to prevent duplicates
      const wasAdded = this.addCourseToFilters(course);

      if (wasAdded) {
        this.components.urlManager.setURLSearchParams();
        this.addLabel(course);
        if (!suppressRefresh) {
          window.refreshProductsDisplay();
        }
      } else {
        // Course already exists, show a brief message or just ignore
      }
    });

    document.addEventListener('sidebar:studentSubmitted', event => {
      const { studentData, onComplete, onError } = event.detail;
      this.handleStudentSubmission(studentData, onComplete, onError);
    });

    // Listen for mobile student ID submission
    document.addEventListener('mobile:studentIdSubmitted', event => {
      const { studentData, onComplete, onError } = event.detail;
      this.handleStudentSubmission(studentData, onComplete, onError, {
        closeMobileDrawerOnlyOnSuccess: true,
      });
    });

    document.addEventListener('sidebar:clearSelections', event => {
      const { clearCampus, clearTerm } = event.detail;
      this.clearAllUserSelections(clearCampus, clearTerm);
    });

    document.addEventListener('sidebar:clearAllTermSelections', event => {
      const { clearTerm } = event.detail;
      this.clearAllTermSelections(clearTerm);
    });

    // Navigation manager initialization on DOM ready
    document.addEventListener('DOMContentLoaded', () => {
      if (this.components.navigationManager) {
        this.components.navigationManager.initializeOnDOMReady();
      } else {
        // Fallback for when navigation manager isn't ready yet
        const navPanel = document.getElementById('right-panel-nav');
        const campusTermBar = document.getElementById('campus-term-info');

        if (navPanel) navPanel.style.display = 'none';
        if (campusTermBar) campusTermBar.style.display = 'none';
      }
    });
  }
  parseURLParameters() {
    
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      // @ts-ignore
      shopBy: urlParams.get(this.URL_PARAMS.SHOP_BY),
      // @ts-ignore
      termId: urlParams.get(this.URL_PARAMS.TERM_ID),
      // @ts-ignore
      divisionDisplayName: urlParams.get(this.URL_PARAMS.DIVISION_DISPLAY_NAME),
      // @ts-ignore
      departmentDisplayName: urlParams.get(
        this.URL_PARAMS.DEPARTMENT_DISPLAY_NAME
      ),
      // @ts-ignore
      courseDisplayName: urlParams.get(this.URL_PARAMS.COURSE_DISPLAY_NAME),
      // @ts-ignore
      sectionDisplayName: urlParams.get(this.URL_PARAMS.SECTION_DISPLAY_NAME),
      // @ts-ignore
      
    };

    const parsed = {
      termsIds: params.termId && params.sectionDisplayName     ? Array(params.sectionDisplayName.split('|').length).fill(params.termId)     : [],
      divisions: params.divisionDisplayName
        ? params.divisionDisplayName.split('|')
        : [],
      departments: params.departmentDisplayName
        ? params.departmentDisplayName.split('|')
        : [],
      courses: params.courseDisplayName
        ? params.courseDisplayName.split('|')
        : [],
      sections: params.sectionDisplayName
        ? params.sectionDisplayName.split('|')
        : [],
    };
    if (
      parsed.termsIds.length === 0 &&
      params.termId &&
      parsed.courseRefIds.length > 0
    ) {
      parsed.termsIds = Array(parsed.courseRefIds.length).fill(params.termId);
    }
    //console.log("urlParamsToRestore ==> ", parsed);
       
    return parsed;
  }
  /**
   * Initialize UI state
   */
  initializeUI() {
    // Only show initial loading when opening the page with URL params
    setTimeout(() => {
      const hasURLParams = window.location.search.length > 1;
      const { termsIds, divisions, departments, courses, sections } =
      this.parseURLParameters();
      const hasURLParamsToRestore = [
        termsIds,
        divisions,
        departments,
        courses,
        sections,
      ].some(arr => arr.length > 0);  
      const shouldSkipInitialLoading = window.__skipInitialCourseLoading === true;
      if (this.components.uiStateManager && hasURLParams && hasURLParamsToRestore && !shouldSkipInitialLoading) {
        this.components.uiStateManager.showLoadingState();
      }
    }, 50);
  }

  /**
   * Handle desktop sidebar auto-opening
   */
  handleDesktopSidebar() {
    setTimeout(() => {
      if (
        document.body.classList.contains('course-materials-page') ||
        window.innerWidth > 968
      ) {
        this.components.sidebar?.openSidePanel?.();
      }
    }, 100);
  }

  /**
   * Add a course label to the UI
   */
  addLabel(objCourse) {
    const courseListElement = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (!courseListElement) return;

    if (
      objCourse.selectedCampus &&
      objCourse.selectedTerm &&
      objCourse.selectedDepartment &&
      objCourse.selectedcourse &&
      objCourse.selectedsection
    ) {
      const courseHTML = `
                <div class="course-item" data-campus="${
                  objCourse.selectedCampus
                }" 
                     data-term="${objCourse.selectedTerm}" data-division="${
        objCourse.selectedDivision || ''
      }" data-department="${
        objCourse.selectedDepartment
      }" 
                     data-course="${objCourse.selectedcourse}" data-section="${
        objCourse.selectedsection
      }"
                     ${
                       objCourse.fromStudentId
                         ? 'data-from-student-id="true"'
                         : ''
                     }>
                    ${
                      objCourse.selectedDivision
                        ? `${objCourse.selectedDivision} | `
                        : ''
                    }${objCourse.selectedDepartment} ${
        objCourse.selectedcourse
      } | ${objCourse.selectedsection} 
                    <span class="remove-course">×</span>
                </div>`;

      courseListElement.insertAdjacentHTML('beforeend', courseHTML);

      // Show the form actions container when first course is added
      if (formActionsContainer && courseListElement.children.length === 1) {
        formActionsContainer.style.display = 'flex';
      }

      const newCourseItem = courseListElement.lastElementChild;
      const removeButton = newCourseItem.querySelector('.remove-course');

      if (removeButton) {
        removeButton.addEventListener('click', () => {
          this.removeCourse(newCourseItem);
        });
      }
    }
  }
  /**
   * Normalize course identity fields for comparisons.
   * @param {unknown} v
   * @returns {string}
   */
  static _trimCourseField(v) {
    return v == null || v === '' ? '' : String(v).trim();
  }

  /** Loose compare for URL-restored vs DOM labels (spaces, underscores, case). */
  static _looseCourseFieldEq(a, b) {
    const n = v =>
      String(v ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
    return n(a) === n(b);
  }

  /**
   * Single entry point: remove one course by department + course number + section (+ optional division).
   * Updates selectedFilters, clears bubbles/tags, re-fetches/renders products via refreshProductsDisplay.
   * If the course is only a pending mobile tag (not yet in selectedFilters), only clears that UI.
   * When division is provided, only that grade/division line is removed.
   */
  removeCourseSelection(department, course, section, division) {
    const t = CourseMaterialsPage._trimCourseField;
    const looseEq = CourseMaterialsPage._looseCourseFieldEq;
    const d = t(department);
    const c = t(course);
    const s = t(section);
    const divProvided = division !== undefined && division !== null;
    const div = divProvided ? t(division) : null;
    console.info('[CMRP remove] removeCourseSelection called', {
      raw: { department, course, section, division },
      normalized: { d, c, s, div },
      filterCount: this.selectedFilters.length,
      filtersSnapshot: this.selectedFilters.map(f => ({
        div: f.selectedDivision ?? f.selecteddivision,
        dept: f.selectedDepartment ?? f.selecteddepartment,
        crs: f.selectedcourse ?? f.course,
        sec: f.selectedsection ?? f.section,
      })),
    });
    if (!d || !c || !s) {
      console.warn('[CMRP remove] abort: empty department, course, or section after trim');
      return;
    }

    const matchDivision = f => {
      if (!divProvided) return true;
      const fdiv = t(f.selectedDivision ?? f.selecteddivision ?? '');
      return fdiv === div || looseEq(fdiv, div);
    };

    const matchTripleExact = f => {
      const fd = t(f.selectedDepartment ?? f.selecteddepartment);
      const fc = t(f.selectedcourse ?? f.course);
      const fs = t(f.selectedsection ?? f.section);
      return fd === d && fc === c && fs === s && matchDivision(f);
    };

    const matchTripleLoose = f =>
      looseEq(f.selectedDepartment ?? f.selecteddepartment, d) &&
      looseEq(f.selectedcourse ?? f.course, c) &&
      looseEq(f.selectedsection ?? f.section, s) &&
      matchDivision(f);

    let filterIndex = this.selectedFilters.findIndex(matchTripleExact);
    if (filterIndex === -1) {
      filterIndex = this.selectedFilters.findIndex(matchTripleLoose);
    }
    if (filterIndex === -1) {
      console.warn(
        '[CMRP remove] no selectedFilters row matched (exact or loose); UI chips cleared only',
        { d, c, s, div }
      );
      window.courseMaterialsSidebar?.clearCourseSelectionUI?.(d, c, s, div);
      void window.courseMaterialsSidebar?.resetCourseBuilderLineKeepCampusTerm?.();
      window.courseMaterialsMobile?.updateMobileSubmitButtonState?.();
      if (!document.querySelector('#mobile-selected-courses .mobile-course-tag')) {
        window.courseMaterialsMobile?.handleEmptyCoursesState?.();
      }
      window.courseMaterialsMobile?.updateRightPanelContent?.();
      return;
    }

    console.info('[CMRP remove] matched filter index', filterIndex);

    const removedFilter = this.selectedFilters[filterIndex];
    this.selectedFilters.splice(filterIndex, 1);

    if (typeof this.components.urlManager?.updateSelectedFilters === 'function') {
      this.components.urlManager.updateSelectedFilters(this.selectedFilters);
    }

    this.pruneSelectionsForRemovedCourse(removedFilter);

    const removedDiv = t(
      removedFilter.selectedDivision ?? removedFilter.selecteddivision ?? '',
    );
    this.removeCourseListItemIfPresent(d, c, s, removedDiv);
    this.updateCourseListFormVisibility();

    const canonD = t(
      removedFilter.selectedDepartment ?? removedFilter.selecteddepartment
    );
    const canonC = t(removedFilter.selectedcourse ?? removedFilter.course);
    const canonS = t(removedFilter.selectedsection ?? removedFilter.section);
    window.courseMaterialsSidebar?.clearCourseSelectionUI?.(
      canonD,
      canonC,
      canonS,
      removedDiv,
    );
    window.courseMaterialsSidebar?.clearCourseSelectionUI?.(d, c, s, removedDiv);

    document.dispatchEvent(
      new CustomEvent('sidebar:courseRemoved', {
        detail: {
          course: {
            selectedDepartment: d,
            selectedcourse: c,
            selectedsection: s,
            selectedDivision: removedDiv,
            selectedCampus:
              removedFilter.selectedCampus ?? removedFilter.selectedcampus,
            selectedTerm: removedFilter.selectedTerm ?? removedFilter.selectedterm,
          },
        },
      })
    );

    console.info('[CMRP remove] syncing URL (setURLSearchParams) then await refreshProductsDisplay');
    this.components.urlManager.setURLSearchParams();

    void (async () => {
      const stillHasCourses = this.selectedFilters.length > 0;
      try {
        if (stillHasCourses) {
          await window.courseMaterialsSidebar?.resetCourseBuilderLineKeepCampusTerm?.();
        }
        await window.refreshProductsDisplay?.();
      } catch (e) {
        console.error('[CMRP remove] refreshProductsDisplay failed', e);
      }
      if (this.selectedFilters.length === 0) {
        console.info(
          '[CMRP remove] no filters left — empty state + mobile tags cleared'
        );
        this.handleEmptySelections();
        this.removeAllMobileTags();
      }
      window.courseMaterialsMobile?.updateMobileSubmitButtonState?.();
    })();
  }

  /**
   * Drop bag/selection rows for products tied to the removed section (by section id).
   * @param {Record<string, unknown>} removedFilter
   */
  pruneSelectionsForRemovedCourse(removedFilter) {
    const sm = this.components?.selectionManager;
    if (!sm?.selectedProducts?.length) return;

    const removedSid = removedFilter?.selectedsectionId;
    if (removedSid == null || removedSid === '') return;

    const arr = window.courseData.products_Materials || [];
    sm.selectedProducts = sm.selectedProducts.filter(sp => {
      const p = arr.find(x => x.id === sp.productId);
      if (!p || p.sectionId == null) return true;
      const ids = Array.isArray(p.sectionId) ? p.sectionId : [p.sectionId];
      return !ids.some(id => String(id) === String(removedSid));
    });
  }

  removeCourseListItemIfPresent(d, c, s, division) {
    const t = CourseMaterialsPage._trimCourseField;
    const list = document.getElementById('course-list');
    if (!list) return;
    const divProvided = division !== undefined && division !== null;
    const div = divProvided ? t(division) : null;
    list.querySelectorAll('.course-item').forEach(el => {
      if (
        t(el.getAttribute('data-department')) === d &&
        t(el.getAttribute('data-course')) === c &&
        t(el.getAttribute('data-section')) === s &&
        (!divProvided || t(el.getAttribute('data-division') || '') === div)
      ) {
        el.remove();
      }
    });
  }

  updateCourseListFormVisibility() {
    const courseList = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (
      courseList &&
      formActionsContainer &&
      courseList.children.length === 0
    ) {
      formActionsContainer.style.display = 'none';
    }
  }

  /**
   * Legacy hook: in-card remove buttons and course-list items use data-* attrs.
   * @param {Element} courseElement
   */
  removeCourse(courseElement) {
    if (!courseElement?.getAttribute) return;
    const t = CourseMaterialsPage._trimCourseField;
    console.info('[CMRP remove] removeCourse (in-card / legacy)', {
      tag: courseElement.tagName,
      class: courseElement.className,
      department: courseElement.getAttribute('data-department'),
      course: courseElement.getAttribute('data-course'),
      section: courseElement.getAttribute('data-section'),
    });
    this.removeCourseSelection(
      t(courseElement.getAttribute('data-department')),
      t(courseElement.getAttribute('data-course')),
      t(courseElement.getAttribute('data-section')),
      t(courseElement.getAttribute('data-division') || ''),
    );
  }
  
  /**
   * Remove all mobile course tags and show empty state
   */
  removeAllMobileTags() {
    const selectedCoursesContainer = document.getElementById('mobile-selected-courses');
    if (selectedCoursesContainer) {
      // Remove all tags
      const allTags = selectedCoursesContainer.querySelectorAll('.mobile-course-tag');
      allTags.forEach(tag => tag.remove());
      
      // Hide container
      selectedCoursesContainer.style.display = 'none';
      
      // Handle empty state
      if (window.courseMaterialsMobile) {
        window.courseMaterialsMobile.handleEmptyCoursesState();
        window.courseMaterialsMobile.updateMobileSubmitButtonState();
      }
    }
  }

  /**
   * Handle empty selections state
   */
  handleEmptySelections() {
    this.components.uiStateManager.showEmptyState();

    const campusSelect =
      document.getElementById('campus-cb') ||
      (typeof window.cmrpQueryDesktopCampusTerm === 'function'
        ? window.cmrpQueryDesktopCampusTerm('campus')
        : document.querySelector('#campus-term-dropdowns #campus'));
    const termSelect =
      document.getElementById('term-cb') ||
      (typeof window.cmrpQueryDesktopCampusTerm === 'function'
        ? window.cmrpQueryDesktopCampusTerm('term')
        : document.querySelector('#campus-term-dropdowns #term'));

    if (campusSelect && campusSelect.value) {
      if (termSelect && !termSelect.value) {
        this.components.sidebar.populateTerms(campusSelect);
      } else if (termSelect && termSelect.value) {
        //this.components.sidebar.clearDDCSSelections(true);
      }
    } else {
      this.components.sidebar.populateCampus();
    }
  }

  /**
   * Test API connectivity
   */
  async testAPIConnectivity(baseUrl) {
    try {
      // Try a simple HEAD request first to test connectivity
      const testResponse = await fetch(baseUrl, {
        method: 'HEAD',
        mode: 'no-cors', // This helps avoid CORS preflight issues
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Show error message in sidebar and mobile-drawer warning components (same markup as `warning-message` snippet).
   */
  showErrorMessage(message, supportingMessage = '', hasButton = false, options = {}) {
    const { noCloseWhenButton = false, warningIds = null } = options;
    const ids =
      Array.isArray(warningIds) && warningIds.length > 0
        ? warningIds
        : [this.warningId, this.mobileWarningId];

    ids.forEach(warningId => {
      const warningElement = document.getElementById(warningId);
      if (!warningElement) return;

      if (
        hasButton &&
        (message === 'This is taking longer than expected' || noCloseWhenButton)
      ) {
        warningElement.classList.add('warning-message--no-close');
      } else {
        warningElement.classList.remove('warning-message--no-close');
      }

      const mainMessageElement = warningElement.querySelector(
        '.warning-message__text--main'
      );
      if (mainMessageElement) mainMessageElement.textContent = message;

      const supportingMessageElement = warningElement.querySelector(
        '.warning-message__text--supporting'
      );
      if (supportingMessageElement) {
        if (supportingMessage) {
          if (hasButton) {
            const buttonText = 'Manually Add Courses';
            const messageWithButton = supportingMessage.replace(
              buttonText,
              `<button class="warning-message__inline-btn" onclick="window.courseMaterialsSidebar?.switchToManualCourseSelection()">${buttonText}</button>`
            );
            supportingMessageElement.innerHTML = messageWithButton;
          } else {
            supportingMessageElement.textContent = supportingMessage;
          }
          supportingMessageElement.style.display = 'block';
        } else {
          supportingMessageElement.style.display = 'none';
        }
      }

      const legacyMessageElement = warningElement.querySelector(
        '.warning-message__text:not(.warning-message__text--main):not(.warning-message__text--supporting)'
      );
      if (legacyMessageElement && !mainMessageElement) {
        legacyMessageElement.textContent = message;
      }

      if (typeof window.showWarningMessage === 'function') {
        window.showWarningMessage(warningId);
      } else {
        warningElement.style.display = 'block';
      }
    });
  }

  /**
   * Hide error message (desktop sidebar + mobile drawer)
   */
  hideErrorMessage() {
    [this.warningId, this.mobileWarningId].forEach(warningId => {
      const warningElement = document.getElementById(warningId);
      if (warningElement) {
        warningElement.classList.remove('warning-message--no-close');
      }
      if (typeof window.hideWarningMessage === 'function') {
        window.hideWarningMessage(warningId);
      } else if (warningElement) {
        warningElement.style.display = 'none';
      }
    });
  }

  /**
   * Validate student ID format
   */
  validateStudentId(studentId) {
    const trimmed = String(studentId ?? '').trim();
    if (trimmed.length === 0) {
      return { valid: false, message: `${cmrpStudentIdLabel()} cannot be empty` };
    }
    return { valid: true, message: '' };
  }

  /**
   * Normalize API discovery status codes (e.g. 7, "007") to three-digit strings.
   * @param {unknown} raw
   * @returns {string|null}
   */
  normalizeStudentDiscoveryStatusCode(raw) {
    if (raw === undefined || raw === null || raw === '') return null;
    const n = parseInt(String(raw).trim(), 10);
    if (Number.isNaN(n)) return null;
    return String(n).padStart(3, '0');
  }

  /**
   * Read statusCode from student discovery API JSON body.
   * @param {object|null|undefined} result
   * @returns {string|null}
   */
  extractStatusCodeFromStudentDiscoveryResponse(result) {
    if (!result || typeof result !== 'object') return null;
    const raw =
      result.statusCode ??
      result.status_code ??
      result.errorCode ??
      result.error?.code ??
      result.data?.statusCode ??
      result.data?.status_code;
    return this.normalizeStudentDiscoveryStatusCode(raw);
  }

  /**
   * Show student-ID discovery errors mapped from API status codes (desktop + mobile warnings).
   * @param {string|null} code - Three-digit normalized code or null
   */
  showStudentIdDiscoveryErrorByStatusCode(code) {
    const supporting = 'Try again or Manually Add Courses.';
    const hasButton = true;
    const opts = { noCloseWhenButton: true };

    if (!code) {
      this.showErrorMessage(
        'Oops! Something went wrong.',
        supporting,
        hasButton,
        opts
      );
      return;
    }

    if (['004', '005', '006', '007'].includes(code)) {
      this.showErrorMessage(
        "We're having trouble verifying your details.",
        supporting,
        hasButton,
        opts
      );
      return;
    }

    if (code === '008') {
      this.showErrorMessage(
        'This is taking longer than expected',
        supporting,
        hasButton,
        opts
      );
      return;
    }

    if (['003', '009', '010'].includes(code)) {
      this.showErrorMessage(
        'Oops! Something went wrong.',
        supporting,
        hasButton,
        opts
      );
      return;
    }

    this.showErrorMessage(
      'Oops! Something went wrong.',
      supporting,
      hasButton,
      opts
    );
  }

  async fetchMetaobjects(metaobjectType, query) {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, query);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }
  /**
   * Handle student submission from sidebar or mobile drawer
   * @param {Object} [flowOptions]
   * @param {boolean} [flowOptions.closeMobileDrawerOnlyOnSuccess] - When true, only call onComplete if courses were returned (mobile)
   */
  async handleStudentSubmission(studentData, onComplete, onError, flowOptions = {}) {
    const { closeMobileDrawerOnlyOnSuccess = false } = flowOptions;
    const { selectedCampus, selectedTerm, studentId } = studentData;
    // Hide any existing error messages first
    this.hideErrorMessage();

    // Clear prior course/student results and product grid before a new Student ID search
    this.clearAllUserSelections(false, false);
    this.components.selectionManager?.clearSelections?.();

    // Enable Student ID mode to hide course selection dropdowns
    if (this.components.sidebar) {
      this.components.sidebar.enableStudentIdMode();
    }

    // Validate student ID first
    const studentIdValidation = this.validateStudentId(studentId);
    if (!studentIdValidation.valid) {
      this.showErrorMessage(
        `Invalid ${cmrpStudentIdLabel()}: ${studentIdValidation.message}`
      );
      onError();
      return;
    }

    const decodeCmrp = v =>
      typeof window.decodeCmrpHtmlEntities === 'function'
        ? window.decodeCmrpHtmlEntities(v)
        : String(v ?? '');
    const normLabel = value =>
      String(decodeCmrp(value ?? ''))
        .toLowerCase()
        .replace(/\s+/g, '');

    const filteredCampus = this.courseData.ddcs_campus_entity.filter(campus =>
      normLabel(campus.name).includes(normLabel(selectedCampus))
    );
    if (!filteredCampus.length) {
      this.showErrorMessage(
        'Campus not found. Please verify your campus selection.'
      );
      onError();
      return;
    }

    const store_number = filteredCampus[0].store_number;
    const campus_entity_id = filteredCampus[0].campus_entity_id.toUpperCase();
    
    if (!selectedTerm) {
      this.showErrorMessage(
        'Term not found. Please verify your term selection.'
      );
      onError();
      return;
    }

    this.components.sidebar?.resetCmrpCourseBuilderRowAfterStudentSubmit?.();

    const endpoint = `${window.shopifySettings.studentIDAPIURL}/api/discovery/store/${store_number}/campus/${campus_entity_id}/term/${selectedTerm.toUpperCase()}/student/${studentId}`;
    console.log('endpoint', endpoint);
    // Test API connectivity first
    const baseApiUrl = window.shopifySettings.studentIDAPIURL;
    const isConnected = await this.testAPIConnectivity(baseApiUrl);

    if (!isConnected) {
      this.showErrorMessage(
        'Unable to connect to the student data service. Please check your internet connection and try again.'
      );
      onError();
      return;
    }

    // Add timeout and better error handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    fetch(endpoint, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })
      .then(async response => {
        clearTimeout(timeoutId);

        let result = null;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            result = await response.json();
          } catch (e) {
            result = null;
          }
        }

        const statusCodeFromBody =
          this.extractStatusCodeFromStudentDiscoveryResponse(result);

        if (!response.ok) {
          if (statusCodeFromBody) {
            this.showStudentIdDiscoveryErrorByStatusCode(statusCodeFromBody);
          } else {
            this.showErrorMessage(
              `There was a problem fetching student data: HTTP ${response.status}: ${response.statusText}`
            );
          }
          onError();
          return;
        }

        if (!result || !result.success) {
          this.showStudentIdDiscoveryErrorByStatusCode(statusCodeFromBody);
          onError();
          return;
        }

        const hasSections =
          result.data &&
          Array.isArray(result.data.sections) &&
          result.data.sections.length > 0;

        if (hasSections) {
          if (this.components.sidebar?.hydrateDdcsDataForCampusTermRow) {
            try {
              await this.components.sidebar.hydrateDdcsDataForCampusTermRow(
                'sid',
                { allowSharedCourseDataOverwrite: true },
              );
            } catch (e) {
              console.error('[CMRP] hydrateDdcsDataForCampusTermRow failed', e);
            }
          }
          const dualCmrp =
            document.getElementById('filters-section')?.dataset
              ?.cmrpStudentIdActive === 'true';
          if (!dualCmrp) {
            document
              .querySelectorAll(
                '.cmrp-filter-form--course-builder .cmrp-course-line-fields',
              )
              .forEach(el => {
                el.style.display = 'none';
              });
          }

          const normalizeCampusName = v =>
            String(v ?? '')
              .toLowerCase()
              .replace(/\s+/g, '');
          const campusEntitySid = window.courseData.ddcs_campus_entity?.find(
            c => normalizeCampusName(c.name) === normalizeCampusName(selectedCampus)
          );
          const campusFlagsSid = campusEntitySid
            ? {
                is_department_used_flag: Boolean(
                  campusEntitySid.is_department_used_flag
                ),
                is_division_used_flag: Boolean(
                  campusEntitySid.is_division_used_flag
                ),
              }
            : null;

          result.data.sections.forEach(section => {
            const departmentRelationship = section.relationships?.find(
              rel => rel.institution_department_id || rel.institution_department_name
            );
            const courseRelationship = section.relationships?.find(
              rel => rel.institution_course_id || rel.course_name || rel.course_number
            );
            const divisionRelationship = section.relationships?.find(
              rel => rel.institution_division_id
            );
            // ProductDisplayManager matches department by name, course by coursenumber, section by name
            const departmentValue = departmentRelationship?.institution_department_name ||
              departmentRelationship?.department_name ||
              departmentRelationship?.institution_department_id ||
              '';
            const courseValue = courseRelationship?.course_number ||
              courseRelationship?.course_code ||
              courseRelationship?.course_name ||
              '';
            const divisionValue =
              divisionRelationship?.institution_division_id || '';
            const useDivisionOnFilter = Boolean(
              campusFlagsSid?.is_division_used_flag
            );

            const objCourse = {
              selectedDepartment: departmentValue,
              selectedCampus: selectedCampus,
              selectedTerm: selectedTerm,
              selectedTermId: selectedTerm,
              selectedcourse: courseValue,
              selectedsection: section.section_number || section.section_id || section.name || '',
              fromStudentId: true, // Mark as coming from Student ID search
              ...(useDivisionOnFilter && divisionValue
                ? { selectedDivision: divisionValue }
                : {}),
            };

            const wasAdded = this.addCourseToFilters(objCourse);
            if (wasAdded) {
              this.addLabel(objCourse);
            }
          });

          this.components.urlManager.setURLSearchParams();
          window.refreshProductsDisplay();
        } else {
          this.showErrorMessage(
            'This is taking longer than expected',
            'Try again or Manually Add Courses.',
            true,
            { noCloseWhenButton: true }
          );
        }

        if (closeMobileDrawerOnlyOnSuccess) {
          if (hasSections) {
            onComplete?.();
          } else {
            onError?.();
          }
        } else {
          onComplete?.();
        }
      })
      .catch(error => {
        clearTimeout(timeoutId);

        let errorMessage = 'There was a problem fetching student data: ';

        if (error.name === 'AbortError') {
          errorMessage += 'Request timed out (30 seconds). Please try again.';
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage +=
            'Network error or CORS issue. Please check your connection.';
        } else if (error.message.includes('HTTP')) {
          errorMessage += error.message;
        } else {
          errorMessage += error.message;
        }

        this.showErrorMessage(errorMessage);
        onError();
      });
  }

  /**
   * Add course to filters, preventing duplicates
   * @param {Object} course - Course object to add
   * @returns {boolean} - True if course was added, false if duplicate
   */
  addCourseToFilters(course) {
    // Validate course object
    if (!course || typeof course !== 'object') {
      console.warn('Invalid course object:', course);
      return false;
    }
    
    // Check if course already exists
    const isDuplicate = this.selectedFilters.some(
      filter =>
        filter.selectedCampus === (course.selectedCampus || course.selectedcampus) &&
        filter.selectedTerm === (course.selectedTerm || course.selectedterm) &&
        (filter.selectedDivision || '') ===
          (course.selectedDivision || course.selecteddivision || '') &&
        filter.selectedDepartment === (course.selectedDepartment || course.selecteddepartment) &&
        filter.selectedcourse === (course.selectedcourse || course.course) &&
        filter.selectedsection === (course.selectedsection || course.section)
    );

    if (isDuplicate) {
      return false;
    }

    this.selectedFilters.push(course);
    return true;
  }

  /**
   * Clear only courses that came from Student ID search
   */
  clearStudentIdCourses() {
    // Remove courses from selectedFilters that came from Student ID
    const coursesToRemove = this.selectedFilters.filter(
      filter => filter.fromStudentId
    );
    this.selectedFilters = this.selectedFilters.filter(
      filter => !filter.fromStudentId
    );

    // Remove corresponding course labels from UI
    const courseList = document.getElementById('course-list');
    if (courseList && coursesToRemove.length > 0) {
      coursesToRemove.forEach(course => {
        const divisionAttr = course.selectedDivision
          ? `[data-division="${CSS.escape(String(course.selectedDivision))}"]`
          : '';
        const courseItem = courseList.querySelector(
          `.course-item${divisionAttr}[data-department="${course.selectedDepartment}"][data-course="${course.selectedcourse}"][data-section="${course.selectedsection}"]`
        );
        if (courseItem) {
          courseItem.remove();
        }
      });

      // Hide form actions container if no courses remain
      const formActionsContainer = document.getElementById(
        'form-actions-container'
      );
      if (formActionsContainer && courseList.children.length === 0) {
        formActionsContainer.style.display = 'none';
      }
    }
  }

  /**
   * Clear all user selections
   */
  clearAllUserSelections(clearCampus = false, clearTerm = false) {
    this.selectedFilters.length = 0;

    const courseList = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (courseList) courseList.innerHTML = '';
    if (formActionsContainer) formActionsContainer.style.display = 'none';

    const filteredProductsEl = document.getElementById('filtered-products');
    if (filteredProductsEl) filteredProductsEl.innerHTML = '';

    this.components.uiStateManager.showEmptyState();

    if (clearCampus) {
      const campusSelect =
        document.getElementById('campus-cb') ||
        (typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('campus')
          : document.querySelector('#campus-term-dropdowns #campus'));
      if (campusSelect) {
        campusSelect.value = '';
        campusSelect.disabled = false;
      }
    }

    if (
      this.components.urlManager &&
      !this.components.urlManager.isUrlRestorePending?.()
    ) {
      this.components.urlManager.setURLSearchParams();
    }

    if (this.components.navigationManager) {
      this.components.navigationManager.hideNavigationPanels();
    } else {
      const navPanel = document.getElementById('right-panel-nav');
      const campusTermBar = document.getElementById('campus-term-info');
      const chipsContainer = document.getElementById('department-filter-chips');
      if (navPanel) navPanel.style.display = 'none';
      if (campusTermBar) campusTermBar.style.display = 'none';
      if (chipsContainer) chipsContainer.innerHTML = '';
    }
    const courses_container = document.getElementById(
      typeof window.cmrpId === 'function'
        ? window.cmrpId('selected-courses-container', 'cb')
        : 'selected-courses-container',
    );
    if (courses_container) courses_container.innerHTML = '';

    this.removeAllMobileTags();
    if (typeof window.courseMaterialsSidebar?.syncBubblesFromFilters === 'function') {
      window.courseMaterialsSidebar.syncBubblesFromFilters([]);
    }
  }

  clearAllTermSelections(clearTerm = false) {
    this.selectedFilters.length = 0;

    const courseList = document.getElementById('course-list');
    const formActionsContainer = document.getElementById(
      'form-actions-container'
    );
    if (courseList) courseList.innerHTML = '';
    if (formActionsContainer) formActionsContainer.style.display = 'none';

    const filteredProductsEl = document.getElementById('filtered-products');
    if (filteredProductsEl) filteredProductsEl.innerHTML = '';

    this.components.uiStateManager.showEmptyState();

    
    if (
      this.components.urlManager &&
      !this.components.urlManager.isUrlRestorePending?.()
    ) {
      this.components.urlManager.setURLSearchParams();
    }

    if (this.components.navigationManager) {
      this.components.navigationManager.hideNavigationPanels();
    } else {
      const navPanel = document.getElementById('right-panel-nav');
      const campusTermBar = document.getElementById('campus-term-info');
      const chipsContainer = document.getElementById('department-filter-chips');
      if (navPanel) navPanel.style.display = 'none';
      if (campusTermBar) campusTermBar.style.display = 'none';
      if (chipsContainer) chipsContainer.innerHTML = '';
    }
    const courses_container = document.getElementById(
      typeof window.cmrpId === 'function'
        ? window.cmrpId('selected-courses-container', 'cb')
        : 'selected-courses-container',
    );
    if (courses_container) courses_container.innerHTML = '';

    this.removeAllMobileTags();
    if (typeof window.courseMaterialsSidebar?.syncBubblesFromFilters === 'function') {
      window.courseMaterialsSidebar.syncBubblesFromFilters([]);
    }
  }

  /**
   * Refresh products display (internal method)
   */
  refreshProductsDisplay() {
    // Call the global method to ensure consistency
    if (window.refreshProductsDisplay) {
      window.refreshProductsDisplay();
    } else {
      this.components.uiStateManager.showLoadingState();
    }
  }

  /**
   * Show campus change warning (for backward compatibility)
   */
  showCampusChangeWarning() {
    if (typeof showWarningMessage === 'function') {
      const wid =
        typeof window.cmrpId === 'function'
          ? window.cmrpId('campus-change-warning', 'activeCt')
          : 'campus-change-warning';
      showWarningMessage(wid);
    }
  }

  /**
   * Hide campus change warning (for backward compatibility)
   */
  hideCampusChangeWarning() {
    if (typeof hideWarningMessage === 'function') {
      const wid =
        typeof window.cmrpId === 'function'
          ? window.cmrpId('campus-change-warning', 'activeCt')
          : 'campus-change-warning';
      hideWarningMessage(wid);
    }
  }
}

// Make the class available globally
window.CourseMaterialsPage = CourseMaterialsPage;
